import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useUser } from "../UserContext"
import { useOrganization } from "../OrganizationContext"
import { supabase } from "../supabaseClient"

const DEFAULT_PREFERENCES = {
  product_updates_opt_in: false,
  marketing_emails_opt_in: false,
}

const PREFERENCE_FIELDS = [
  { field: "product_updates_opt_in", label: "Product updates" },
  { field: "marketing_emails_opt_in", label: "Marketing emails" },
]

function buildDisplayName(user) {
  if (!user) return ""
  const metadata = user.user_metadata || user.raw_user_meta_data || {}
  const firstName =
    metadata.first_name ||
    metadata.firstName ||
    metadata.given_name ||
    (metadata.full_name || metadata.name || "").split(" ")[0]
  const lastName =
    metadata.last_name ||
    metadata.lastName ||
    metadata.family_name ||
    (() => {
      const parts = (metadata.full_name || metadata.name || "").trim().split(/\s+/)
      return parts.length > 1 ? parts[parts.length - 1] : ""
    })()

  if (firstName && lastName) return `${firstName} ${lastName}`
  if (metadata.full_name || metadata.name) return metadata.full_name || metadata.name
  return user.email || ""
}

export default function Account() {
  const user = useUser()
  const navigate = useNavigate()
  const { organizations } = useOrganization()
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES)
  const [loadingPreferences, setLoadingPreferences] = useState(Boolean(user?.id))
  const [savingFields, setSavingFields] = useState({})
  const [preferenceError, setPreferenceError] = useState("")

  const displayName = useMemo(() => buildDisplayName(user), [user])

  useEffect(() => {
    let isActive = true

    setSavingFields({})

    if (!user?.id) {
      setPreferences(DEFAULT_PREFERENCES)
      setLoadingPreferences(false)
      setPreferenceError("")
      return () => {
        isActive = false
      }
    }

    const loadPreferences = async () => {
      setLoadingPreferences(true)
      setPreferenceError("")
      const { data, error } = await supabase
        .from("user_email_preferences")
        .select("product_updates_opt_in, marketing_emails_opt_in")
        .eq("user_id", user.id)
        .maybeSingle()

      if (!isActive) return

      if (error) {
        console.error("Failed to load email preferences", error)
        setPreferenceError("Unable to load communication preferences right now.")
        setPreferences(DEFAULT_PREFERENCES)
      } else if (data) {
        setPreferences({
          product_updates_opt_in: Boolean(data.product_updates_opt_in),
          marketing_emails_opt_in: Boolean(data.marketing_emails_opt_in),
        })
      } else {
        setPreferences(DEFAULT_PREFERENCES)
      }

      setLoadingPreferences(false)
    }

    loadPreferences()

    return () => {
      isActive = false
    }
  }, [user?.id])

  const handlePreferenceToggle = async (field) => {
    if (!user?.id) return

    const updated = {
      ...preferences,
      [field]: !preferences[field],
    }

    setSavingFields((prev) => ({ ...prev, [field]: true }))
    setPreferenceError("")

    const { error } = await supabase
      .from("user_email_preferences")
      .upsert(
        [
          {
            user_id: user.id,
            product_updates_opt_in: updated.product_updates_opt_in,
            marketing_emails_opt_in: updated.marketing_emails_opt_in,
          },
        ],
        { onConflict: "user_id" }
      )

    if (error) {
      console.error("Failed to update email preference", error, { field })
      setPreferenceError("We couldn't save your preference. Please try again.")
      setSavingFields((prev) => ({ ...prev, [field]: false }))
      return
    }

    setPreferences(updated)
    setSavingFields((prev) => ({ ...prev, [field]: false }))
  }

  return (
    <div className="page-container">
      <h1>Account</h1>
      <div style={{ marginBottom: "1.5rem" }}>
        {displayName ? (
          <p>
            Signed in as <strong>{displayName}</strong>
            {user?.email && displayName !== user.email && ` (${user.email})`}
          </p>
        ) : (
          <p>Signed in user information is unavailable.</p>
        )}
      </div>
      <div style={{ marginBottom: "2rem" }}>
        <h2>Communication Preferences</h2>
        {!user?.id ? (
          <p>Sign in to manage your communication preferences.</p>
        ) : loadingPreferences ? (
          <p>Loading preferences...</p>
        ) : (
          <>
            {preferenceError && (
              <p
                role="alert"
                style={{
                  color: "var(--error-color, #c00)",
                  marginBottom: "0.5rem",
                }}
              >
                {preferenceError}
              </p>
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              {PREFERENCE_FIELDS.map(({ field, label }) => (
                <label
                  key={field}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    color: "var(--text-color)",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(preferences[field])}
                    onChange={() => handlePreferenceToggle(field)}
                    disabled={Boolean(savingFields[field])}
                  />
                  <span>{label}</span>
                  {savingFields[field] && (
                    <span
                      style={{
                        fontSize: "0.85rem",
                        color: "var(--muted-text-color, #555)",
                      }}
                    >
                      Saving…
                    </span>
                  )}
                </label>
              ))}
            </div>
          </>
        )}
      </div>
      <div style={{ marginBottom: "2rem" }}>
        <h2>Organization</h2>
        {organizations.length > 0 ? (
          <p>You have access to {organizations.length} organization{organizations.length > 1 ? "s" : ""}.</p>
        ) : (
          <p>You are not currently a member of any organizations.</p>
        )}
        <button onClick={() => navigate("/organizations/manage")}>Manage Organization</button>
      </div>
    </div>
  )
}
